<template>
  <div class="upload-excel-block" style="height: 100%;">
    <input ref="excel-upload-input" :multiple="multiple" class="excel-upload-input" type="file" accept=".xlsx, .xls" @change="handleClick">
    <div :class="dragState" class="drop" @drop="handleDrop" @dragover="handleDragover" @dragenter="handleDragover" @dragleave="handleDragleave">
      <span>{{ title }}</span>
      <el-button :loading="loading" style="margin-left:16px;" size="mini" type="primary" @click="handleUpload">
        {{ $t('common.browse') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx'

let defaultExcelData = {
  header: null,
  props: null,
  custprops: null,
  comments: null,
  results: null
}

export default {
  props: {
    beforeUpload: Function, // eslint-disable-line
    jsonOptions: Object, // eslint-disable-line
    jsonSheetOptions: Object, // eslint-disable-line
    onSuccess: Function, // eslint-disable-line
    multiple: Boolean, // eslint-disable-line
    title: {type: String, default: 'Перетащите excel файл или'},
    sheetNames: [Object, String, Array] // eslint-disable-line
  },
  data () {
    return {
      loading: false,
      excelFiles: {},
      excelData: {
        header: null,
        props: null,
        custprops: null,
        comments: null,
        results: null
      },
      dragState: ''
    }
  },
  methods: {
    generateData ({ file, header, props, custprops, results }) {
      this.excelData = Object.assign({}, defaultExcelData)
      this.excelData.file = file
      // this.excelData.header = header
      this.excelData.props = props
      this.excelData.custprops = custprops
      this.excelData.results = results

      ;(!this.multiple) && this.onSuccess && this.onSuccess(this.excelData)
    },
    handleDrop (e) {
      this.dragState = ''
      e.stopPropagation()
      e.preventDefault()
      if (this.loading) return
      const files = e.dataTransfer.files
      const total = files.length
      if (!this.multiple && total !== 1) {
        this.$message.error('Only support uploading one file!')
        return
      }
      let rawFiles = Array.from(files)
      let rawFile
      for (let index = 0; index < total; index++) {
        rawFile = rawFiles[index]

        if (!this.isExcel(rawFile)) {
          this.$message.error('Only supports upload .xlsx, .xls, .csv suffix files')
          return false
        }
        this.upload(rawFile, index, total)
      }
      e.stopPropagation()
      e.preventDefault()
    },
    handleDragover (e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
      this.dragState = 'dragging'
    },
    handleDragleave (e) {
      this.dragState = ''
    },
    handleUpload () {
      this.$refs['excel-upload-input'].click()
    },
    handleClick (e) {
      const files = e.target.files
      const total = files.length

      this.excelFiles = {}

      let promises = []

      let rawFiles = Array.from(files)
      let rawFile
      if (!this.multiple) {
        rawFile = rawFiles[0] // only use files[0]
        if (!rawFile) return
        promises.push(this.upload(rawFile))
      } else {
        for (let index = 0; index < total; index++) {
          rawFile = rawFiles[index]
          promises.push(this.upload(rawFile, index, total))
        }
      }

      Promise.all(promises).then(() => {
        (this.multiple) && this.onSuccess && this.onSuccess(this.excelFiles)
      })
    },
    upload (rawFile, index = 0, total = 1) {
      this.$refs['excel-upload-input'].value = null // fix can't select the same excel

      if (!this.beforeUpload) {
        return this.readerData(rawFile, index, total)
      }
      let before = this.beforeUpload(rawFile, index, total)
      if (before) {
        return this.readerData(rawFile, index, total)
      }
      return Promise.resolve()
    },
    readerData (rawFile, index, total) {
      this.loading = true
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'array' })

          let sheetNames
          if (this.sheetNames) {
            if (Array.isArray(this.sheetNames)) {
              sheetNames = workbook.SheetNames.filter(n => this.sheetNames.includes(n))
            } else if (typeof (this.sheetNames) === 'string') {
              const regex = new RegExp(this.sheetNames, 'g')
              sheetNames = workbook.SheetNames.filter(n => n.match(regex))
            }
          } else {
            const firstSheetName = workbook.SheetNames[0]
            sheetNames = [ firstSheetName ]
          }

          let results = {}
          for (let idx in sheetNames) {
            let sheetName = sheetNames[idx]
            const worksheet = workbook.Sheets[sheetName]
            results[sheetName] = this.readSheet(sheetName, idx, worksheet)
          }

          const file = {index, total, name: rawFile.name, size: rawFile.size, lastModifiedDate: rawFile.lastModifiedDate}
          this.generateData({ file, props: workbook.Props, custprops: workbook.Custprops, results })
          this.loading = false

          this.excelFiles[rawFile.name] = this.excelData

          resolve()
        }
        reader.readAsArrayBuffer(rawFile)
      })
    },
    readSheet (sheetName, idx, worksheet) {
      const header = this.getHeaderRow(worksheet)
      const defaultJsonOptions = { header: 1, parseComments: false }
      let options = Object.assign(defaultJsonOptions, this.jsonOptions)
      if (this.jsonSheetOptions && this.jsonSheetOptions[sheetName]) {
        options = Object.assign(options, this.jsonSheetOptions[sheetName])
      } else if (this.jsonSheetOptions && this.jsonSheetOptions[idx]) {
        options = Object.assign(options, this.jsonSheetOptions[idx])
      }
      const comments = options.parseComments ? this.getCommentsOnSheet(worksheet) : null
      const results = XLSX.utils.sheet_to_json(worksheet, options)

      return { sheetName, header, comments, results }
    },
    getHeaderRow (sheet) {
      const headers = []
      if (!sheet['!ref']) {
        return headers
      }
      const range = XLSX.utils.decode_range(sheet['!ref'])
      let C
      const R = range.s.r
      /* start in the first row */
      for (C = range.s.c; C <= range.e.c; ++C) { /* walk every column in the range */
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]
        /* find the cell in the first row */
        let hdr = 'UNKNOWN ' + C // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell)
        headers.push(hdr)
      }
      return headers
    },
    getCommentsOnSheet (sheet) {
      return Object.keys(sheet).reduce((acc, k) => {
        let cell = sheet[k]
        if (typeof (cell) === 'object' && cell.c != null) {
          acc[k] = cell.c
        }
        return acc
      }, {})
    },
    isExcel (file) {
      return /\.(xlsx|xls|csv)$/.test(file.name)
    }
  }
}
</script>

<style scoped>
.excel-upload-input{
  display: none;
  z-index: -9999;
}
.drop{
  border: 2px dashed #bbb;
  /*width: 100%;*/
  height: 100%;
  /*margin: 0 auto;*/
  font-size: larger;
  border-radius: 5px;
  /*text-align: center;*/
  color: #bbb;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: height 0.05s ease-in-out, width 0.05s ease-in-out, margin-left 0.05s ease-in-out, margin-top 0.05s ease-in-out, margin-right 0.05s ease-in-out;
}
.drop.dragging {
  background-color: #2f4f4f14;
  /*margin-left: -5px;*/
  /*margin-top: -5px;*/
  /*margin-right: -5px;*/
  height: calc(100% + 10px);
}
  .upload-excel-block {
    height: 100%;
    padding: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/UploadExcel/index.vue